import { STOREFRONT_TRANSITION_DURATION } from "@tablescape/constants";
import { type TClasses, type TTailwindString, tw } from "classnames";

import { type ButtonType, type Size } from "./Button.view";

const buttonBaseStyles = ({
  disabled,
  fluid,
  fontName = "font-calibre",
  fontTracking = "tracking-widest",
  fontWeight = "font-semibold",
  inline,
  px = ["px-4"],
  py = ["py-0"],
  rounded,
  size,
  textTransform = "uppercase",
}: {
  disabled: boolean;
  fluid: boolean;
  fontName?: "font-calibre";
  fontTracking?: "tracking-normal" | "tracking-widest";
  fontWeight?: "font-normal" | "font-semibold";
  inline: boolean;
  px?: TClasses[];
  py?: TClasses[];
  rounded: boolean;
  size: Size;
  textTransform?: "uppercase" | "normal-case";
}) => {
  const sizeStyles = {
    large: tw("h-12"),
    small: tw("h-8", "items-center"),
    pill: tw("h-6"),
  };
  const inlineStyles = tw("border-0", "leading-4", "px-0");
  const noInlineStyles = tw(
    "border-2",
    "border-solid",
    ...px,
    "leading-none",
    "items-center"
  );
  const fluidStyles = tw("w-full");
  return tw(
    {
      "cursor-pointer": !disabled,
      "cursor-not-allowed": disabled,
    },
    fontName,
    fontWeight,
    "antialiased",
    "text-base",
    fontTracking,
    textTransform,
    {
      rounded,
    },
    "inline-flex",
    "justify-center",
    "box-border",
    ...py,
    "transition-all",
    STOREFRONT_TRANSITION_DURATION,
    "focus:outline-none",
    "focus-visible:ring",
    "focus:ring-offset-1",
    "focus:ring-2",
    "focus:ring-interactive-outline",
    sizeStyles[size],
    inline ? inlineStyles : noInlineStyles,
    {
      [fluidStyles]: fluid,
    }
  );
};

const primaryButtonStyles = ({
  inherit,
  type,
}: {
  inherit: boolean;
  type: ButtonType;
}) => {
  const isAlt = type === "primary-alt";

  const base = tw(
    "border-transparent",
    "disabled:bg-interactive-disabled",
    "disabled:text-interactive-disabled-text",
    inherit ? "text-current" : "text-white"
  );
  const typeStyles = isAlt
    ? tw(
        "bg-interactive-brick",
        "active:bg-interactive-brick-pressed",
        "hover:bg-interactive-brick-hover"
      )
    : tw(
        "bg-interactive-char",
        "active:bg-interactive-char-pressed",
        "hover:bg-interactive-char-hover"
      );
  return tw(base, typeStyles);
};

const secondaryButtonStyles = ({
  inherit,
  inline,
  type,
}: {
  inherit: boolean;
  inline: boolean;
  type: ButtonType;
}) => {
  const isAlt = type === "secondary-alt";
  const noInheritStyles = isAlt ? "text-white" : "text-interactive-char";
  const noInlineStyles = tw(
    "disabled:bg-interactive-disabled",
    "disabled:border-interactive-disabled-pressed",
    isAlt ? "hover:bg-white-hover" : "hover:bg-interactive-char-hover/10"
  );
  const noInheritNoInlineStyles = isAlt
    ? tw("border-white", "active:bg-white-pressed", "hover:text-white")
    : tw(
        "border-interactive-char",
        "active:bg-interactive-bone-pressed",
        "active:text-interactive-char-pressed",
        "hover:text-interactive-char-hover"
      );

  const typeStyles = tw(
    "disabled:text-interactive-disabled-text",
    inherit ? "text-current" : noInheritStyles,
    inline ? "hover:underline" : noInlineStyles,
    {
      [noInheritNoInlineStyles]: !inherit && !inline,
    }
  );
  const hasWhiteBackground = type === "secondary-white";
  const background = tw(hasWhiteBackground ? "bg-white" : "bg-transparent");

  return tw(background, typeStyles);
};

const quaternaryButtonStyles = ({ rounded }: { rounded: boolean }) => {
  return tw(
    {
      "rounded-5xl": rounded,
    },
    "flex",
    "items-center",
    "justify-center",
    "text-char-900",
    "bg-char-50",
    "hover:bg-char-100",
    "active:bg-char-200",
    "h-[33px]" as TTailwindString,
    "hover:bg-char-100",
    "active:bg-char-200",
    "disabled:border-interactive-disabled-pressed",
    "disabled:text-interactive-disabled-text",
    "disabled:bg-transparent"
  );
};

const quaternaryAltButtonStyles = ({ rounded }: { rounded: boolean }) => {
  return tw(
    {
      "rounded-5xl": rounded,
    },
    "flex",
    "items-center",
    "justify-center",
    "text-char-50", // Inverted text color (white instead of char-900)
    "bg-char-900", // Inverted background color (dark instead of char-50)
    "hover:bg-char-800", // Inverted hover state
    "h-[33px]" as TTailwindString,
    "border-char-900",
    "disabled:border-interactive-disabled-pressed",
    "disabled:text-interactive-disabled-text",
    "disabled:bg-transparent"
  );
};

const pillButtonStyles = ({
  rounded,
  type,
}: {
  rounded: boolean;
  type: ButtonType;
}) => {
  const isChar = type === "pill-char";
  const isBrick = type === "pill-brick";
  const isOutline = type === "pill-outline";

  return tw(
    "text-xs",
    "h-6",
    {
      "rounded-5xl": rounded,
    },
    {
      [tw(
        "bg-interactive-char",
        "border-interactive-char",
        "text-white",

        "hover:bg-interactive-char-hover",
        "hover:border-interactive-char-hover",

        "active:bg-interactive-char-pressed",

        "disabled:border-interactive-disabled-pressed",
        "disabled:text-interactive-disabled-text",
        "disabled:bg-transparent"
      )]: isChar,
      [tw(
        "bg-interactive-brick",
        "border-interactive-brick",
        "text-white",

        "hover:bg-interactive-brick-hover",
        "hover:border-interactive-brick-hover",

        "active:bg-interactive-brick-pressed",

        "disabled:border-interactive-disabled-pressed",
        "disabled:text-interactive-disabled-text",
        "disabled:bg-transparent"
      )]: isBrick,
      [tw(
        "bg-white",
        "border-primary",
        "text-primary",

        "hover:bg-interactive-char-hover/10",

        "active:bg-interactive-char-hover/20",

        "disabled:border-interactive-disabled-pressed",
        "disabled:text-interactive-disabled-text",
        "disabled:bg-transparent"
      )]: isOutline,
    }
  );
};

export const buttonStyles = ({
  disabled,
  fluid,
  hasIcon,
  inherit,
  inline,
  rounded,
  size,
  type,
}: {
  disabled: boolean;
  fluid: boolean;
  hasIcon: boolean;
  inherit: boolean;
  inline: boolean;
  rounded: boolean;
  size: Size;
  type: ButtonType;
}) => {
  const base = buttonBaseStyles({ disabled, rounded, size, inline, fluid });

  if (type.startsWith("primary")) {
    return tw(base, primaryButtonStyles({ type, inherit }));
  }
  if (type.startsWith("secondary")) {
    return tw(base, secondaryButtonStyles({ type, inherit, inline }));
  }
  if (type === "tertiary") {
    return tw(
      buttonBaseStyles({ disabled, fluid, inline: true, rounded, size }),
      secondaryButtonStyles({ type, inherit, inline: true })
    );
  }
  if (type === "quaternary") {
    return tw(
      buttonBaseStyles({
        disabled,
        size,
        inline,
        fluid,
        rounded,
        fontTracking: "tracking-normal",
        fontWeight: "font-normal",
        textTransform: "normal-case",
        px: ["px-4"],
        py: ["py-2"],
      }),
      quaternaryButtonStyles({ rounded })
    );
  }
  if (type === "quaternary-alt") {
    return tw(
      buttonBaseStyles({
        disabled,
        size,
        inline,
        fluid,
        rounded,
        fontTracking: "tracking-normal",
        fontWeight: "font-normal",
        textTransform: "normal-case",
        px: ["px-4"],
        py: ["py-2"],
      }),
      quaternaryAltButtonStyles({ rounded })
    );
  }
  if (type.startsWith("pill")) {
    return tw(
      buttonBaseStyles({
        disabled,
        size,
        inline,
        fluid,
        rounded,
        px: type === "pill-outline" && hasIcon ? ["px-1.5"] : ["px-2"],
      }),
      pillButtonStyles({ rounded, type })
    );
  }
  if (type === "link") {
    return tw(
      "font-calibre",
      "antialiased",
      "text-button-secondary",
      "hover:text-button-secondary-hover",
      "active:text-button-secondary-pressed",
      "underline",
      "decoration-1",
      "text-base",
      "p-0",
      "border-0",
      "outline-none",
      "bg-transparent",
      "cursor-pointer"
    );
  }
};
